import { useState } from 'react';
import { Calculator, Building, Hammer } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const AMORTIZATION_TERMS = [
  { value: 1, label: '1 Year' },
  { value: 1.5, label: '18 Months' },
  { value: 2, label: '2 Years' },
  { value: 3, label: '3 Years' },
  { value: 5, label: '5 Years' },
  { value: 7, label: '7 Years' },
  { value: 10, label: '10 Years' },
  { value: 20, label: '20 Years' },
  { value: 25, label: '25 Years' },
  { value: 30, label: '30 Years' },
  { value: 50, label: '50 Years' }
];

export default function AmortizationCalculator() {
  const [activeTab, setActiveTab] = useState('permanent');

  // Permanent Financing State
  const [permanent, setPermanent] = useState({
    loan_amount: 200000,
    interest_rate: 7.5,
    term_years: 30,
    monthly_rent: 1800,
    annual_taxes: 3600,
    annual_insurance: 1800
  });

  // Interest Only / Construction Loan State
  const [interestOnly, setInterestOnly] = useState({
    loan_amount: 250000,
    interest_rate: 9.0,
    term_years: 3,
    monthly_rent: 0,
    annual_taxes: 3600,
    annual_insurance: 1800
  });

  // Calculate Monthly P&I Payment (Permanent Financing)
  const calculateMonthlyPI = (principal, annualRate, years) => {
    if (principal <= 0 || annualRate <= 0 || years <= 0) return 0;
    const monthlyRate = annualRate / 100 / 12;
    const numPayments = years * 12;
    const payment = principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / 
                   (Math.pow(1 + monthlyRate, numPayments) - 1);
    return payment;
  };

  // Calculate Interest Only Payment
  const calculateInterestOnly = (principal, annualRate) => {
    if (principal <= 0 || annualRate <= 0) return 0;
    return (principal * (annualRate / 100)) / 12;
  };

  // Permanent Financing Calculations
  const permMonthlyPI = calculateMonthlyPI(permanent.loan_amount, permanent.interest_rate, permanent.term_years);
  const permMonthlyTaxes = permanent.annual_taxes / 12;
  const permMonthlyInsurance = permanent.annual_insurance / 12;
  const permTotalDebtService = permMonthlyPI + permMonthlyTaxes + permMonthlyInsurance;
  const permNetCashFlow = permanent.monthly_rent - permTotalDebtService;
  const permAnnualDebtService = permTotalDebtService * 12;
  const permAnnualNetCashFlow = permNetCashFlow * 12;

  // Interest Only Calculations
  const ioMonthlyInterest = calculateInterestOnly(interestOnly.loan_amount, interestOnly.interest_rate);
  const ioMonthlyTaxes = interestOnly.annual_taxes / 12;
  const ioMonthlyInsurance = interestOnly.annual_insurance / 12;
  const ioTotalDebtService = ioMonthlyInterest + ioMonthlyTaxes + ioMonthlyInsurance;
  const ioNetCashFlow = interestOnly.monthly_rent - ioTotalDebtService;
  const ioAnnualDebtService = ioTotalDebtService * 12;
  const ioAnnualNetCashFlow = ioNetCashFlow * 12;

  // Generate Amortization Schedule (first few years summary)
  const generateAmortizationSummary = (principal, annualRate, years, isInterestOnly = false) => {
    if (principal <= 0 || years <= 0) return [];
    
    const monthlyRate = annualRate / 100 / 12;
    const monthlyPayment = isInterestOnly 
      ? calculateInterestOnly(principal, annualRate)
      : calculateMonthlyPI(principal, annualRate, years);
    
    let balance = principal;
    const yearlyData = [];
    
    for (let year = 1; year <= Math.min(years, 10); year++) {
      let yearInterest = 0;
      let yearPrincipal = 0;
      
      for (let month = 1; month <= 12; month++) {
        const interestPayment = balance * monthlyRate;
        const principalPayment = isInterestOnly ? 0 : monthlyPayment - interestPayment;
        
        yearInterest += interestPayment;
        yearPrincipal += principalPayment;
        balance = Math.max(0, balance - principalPayment);
      }
      
      yearlyData.push({
        year,
        principal: yearPrincipal,
        interest: yearInterest,
        balance: balance
      });
    }
    
    return yearlyData;
  };

  const permSchedule = generateAmortizationSummary(permanent.loan_amount, permanent.interest_rate, permanent.term_years);
  const ioSchedule = generateAmortizationSummary(interestOnly.loan_amount, interestOnly.interest_rate, interestOnly.term_years, true);

  return (
    <div className="bg-zinc-950 border border-zinc-800 p-6">
      <div className="flex items-center gap-2 mb-6">
        <Calculator className="w-5 h-5 text-green-500" />
        <h2 className="text-lg font-mono uppercase tracking-wider text-green-500">
          Amortization & Cash Flow Calculator
        </h2>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-2 bg-zinc-900 rounded-none mb-6 p-2 gap-3 h-auto">
          <TabsTrigger 
            value="permanent" 
            className="font-mono text-xs uppercase py-4 data-[state=active]:bg-green-600 data-[state=active]:text-black rounded-none"
          >
            <Building className="w-4 h-4 mr-2" />
            Permanent Financing
          </TabsTrigger>
          <TabsTrigger 
            value="interest-only" 
            className="font-mono text-xs uppercase py-4 data-[state=active]:bg-green-600 data-[state=active]:text-black rounded-none"
          >
            <Hammer className="w-4 h-4 mr-2" />
            Interest Only
          </TabsTrigger>
        </TabsList>

        {/* PERMANENT FINANCING TAB */}
        <TabsContent value="permanent" className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs font-mono uppercase text-zinc-400">Loan Amount</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                <Input
                  type="number"
                  value={permanent.loan_amount}
                  onChange={(e) => setPermanent({...permanent, loan_amount: parseFloat(e.target.value) || 0})}
                  className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none pl-8 h-12 text-base"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase text-zinc-400">Interest Rate</Label>
              <div className="relative mt-1">
                <Input
                  type="number"
                  step="0.125"
                  value={permanent.interest_rate}
                  onChange={(e) => setPermanent({...permanent, interest_rate: parseFloat(e.target.value) || 0})}
                  className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none pr-10 h-12 text-base"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500">%</span>
              </div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase text-zinc-400">Amortization Term</Label>
              <Select 
                value={permanent.term_years.toString()} 
                onValueChange={(v) => setPermanent({...permanent, term_years: parseInt(v)})}
              >
                <SelectTrigger className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none mt-1 h-12 text-base">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {AMORTIZATION_TERMS.map(term => (
                    <SelectItem key={term.value} value={term.value.toString()}>{term.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-xs font-mono uppercase text-zinc-400">Monthly Rent</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                <Input
                  type="number"
                  value={permanent.monthly_rent}
                  onChange={(e) => setPermanent({...permanent, monthly_rent: parseFloat(e.target.value) || 0})}
                  className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none pl-8 h-12 text-base"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase text-zinc-400">Annual Taxes</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                <Input
                  type="number"
                  value={permanent.annual_taxes}
                  onChange={(e) => setPermanent({...permanent, annual_taxes: parseFloat(e.target.value) || 0})}
                  className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none pl-8 h-12 text-base"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase text-zinc-400">Annual Insurance</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                <Input
                  type="number"
                  value={permanent.annual_insurance}
                  onChange={(e) => setPermanent({...permanent, annual_insurance: parseFloat(e.target.value) || 0})}
                  className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none pl-8 h-12 text-base"
                />
              </div>
            </div>
          </div>

          {/* Debt Service & Cash Flow Summary */}
          <div className="bg-zinc-900 border border-green-500/50 p-4 space-y-3">
            <h4 className="text-xs font-mono uppercase text-green-500 tracking-wider">Debt Service & Cash Flow</h4>
            
            <div className="grid grid-cols-2 gap-6">
              {/* Monthly Column */}
              <div className="space-y-2">
                <p className="text-xs font-mono text-zinc-500 uppercase border-b border-zinc-800 pb-1">Monthly</p>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Rent Income</span>
                  <span className="font-mono text-green-400">${permanent.monthly_rent.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">P&I Payment</span>
                  <span className="font-mono text-red-400">-${permMonthlyPI.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Taxes</span>
                  <span className="font-mono text-red-400">-${permMonthlyTaxes.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Insurance</span>
                  <span className="font-mono text-red-400">-${permMonthlyInsurance.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm pt-2 border-t border-zinc-800">
                  <span className="text-zinc-400">Total Debt Service</span>
                  <span className="font-mono font-bold">${permTotalDebtService.toFixed(2)}</span>
                </div>
                <div className={`flex justify-between text-lg pt-2 ${permNetCashFlow >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  <span className="font-mono uppercase">Net Cash Flow</span>
                  <span className="font-mono font-bold">${permNetCashFlow.toFixed(2)}</span>
                </div>
              </div>

              {/* Annual Column */}
              <div className="space-y-2">
                <p className="text-xs font-mono text-zinc-500 uppercase border-b border-zinc-800 pb-1">Annualized</p>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Rent Income</span>
                  <span className="font-mono text-green-400">${(permanent.monthly_rent * 12).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">P&I Payment</span>
                  <span className="font-mono text-red-400">-${(permMonthlyPI * 12).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Taxes</span>
                  <span className="font-mono text-red-400">-${permanent.annual_taxes.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Insurance</span>
                  <span className="font-mono text-red-400">-${permanent.annual_insurance.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm pt-2 border-t border-zinc-800">
                  <span className="text-zinc-400">Total Debt Service</span>
                  <span className="font-mono font-bold">${permAnnualDebtService.toFixed(2)}</span>
                </div>
                <div className={`flex justify-between text-lg pt-2 ${permAnnualNetCashFlow >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  <span className="font-mono uppercase">Net Cash Flow</span>
                  <span className="font-mono font-bold">${permAnnualNetCashFlow.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Amortization Schedule Summary */}
          <div className="bg-zinc-900 border border-zinc-800 p-4">
            <h4 className="text-xs font-mono uppercase text-zinc-400 tracking-wider mb-3">
              Amortization Schedule ({permanent.term_years} Year Term)
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm font-mono">
                <thead>
                  <tr className="text-xs text-zinc-500 uppercase border-b border-zinc-800">
                    <th className="text-left py-2">Year</th>
                    <th className="text-right py-2">Principal</th>
                    <th className="text-right py-2">Interest</th>
                    <th className="text-right py-2">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {permSchedule.map(row => (
                    <tr key={row.year} className="border-b border-zinc-800/50">
                      <td className="py-2">{row.year}</td>
                      <td className="text-right text-green-400">${row.principal.toFixed(0)}</td>
                      <td className="text-right text-red-400">${row.interest.toFixed(0)}</td>
                      <td className="text-right">${row.balance.toFixed(0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* INTEREST ONLY / CONSTRUCTION LOAN TAB */}
        <TabsContent value="interest-only" className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs font-mono uppercase text-zinc-400">Loan Amount</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                <Input
                  type="number"
                  value={interestOnly.loan_amount}
                  onChange={(e) => setInterestOnly({...interestOnly, loan_amount: parseFloat(e.target.value) || 0})}
                  className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none pl-8 h-12 text-base"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase text-zinc-400">Interest Rate</Label>
              <div className="relative mt-1">
                <Input
                  type="number"
                  step="0.125"
                  value={interestOnly.interest_rate}
                  onChange={(e) => setInterestOnly({...interestOnly, interest_rate: parseFloat(e.target.value) || 0})}
                  className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none pr-10 h-12 text-base"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500">%</span>
              </div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase text-zinc-400">Loan Term</Label>
              <Select 
                value={interestOnly.term_years.toString()} 
                onValueChange={(v) => setInterestOnly({...interestOnly, term_years: parseInt(v)})}
              >
                <SelectTrigger className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none mt-1 h-12 text-base">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {AMORTIZATION_TERMS.map(term => (
                    <SelectItem key={term.value} value={term.value.toString()}>{term.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-xs font-mono uppercase text-zinc-400">Monthly Rent (if any)</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                <Input
                  type="number"
                  value={interestOnly.monthly_rent}
                  onChange={(e) => setInterestOnly({...interestOnly, monthly_rent: parseFloat(e.target.value) || 0})}
                  className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none pl-8 h-12 text-base"
                />
              </div>
              <p className="text-xs text-zinc-600 mt-1">During construction/rehab</p>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase text-zinc-400">Annual Taxes</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                <Input
                  type="number"
                  value={interestOnly.annual_taxes}
                  onChange={(e) => setInterestOnly({...interestOnly, annual_taxes: parseFloat(e.target.value) || 0})}
                  className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none pl-8 h-12 text-base"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase text-zinc-400">Annual Insurance</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                <Input
                  type="number"
                  value={interestOnly.annual_insurance}
                  onChange={(e) => setInterestOnly({...interestOnly, annual_insurance: parseFloat(e.target.value) || 0})}
                  className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono rounded-none pl-8 h-12 text-base"
                />
              </div>
            </div>
          </div>

          {/* Interest Only Debt Service & Cash Flow Summary */}
          <div className="bg-zinc-900 border border-yellow-500/50 p-4 space-y-3">
            <h4 className="text-xs font-mono uppercase text-yellow-500 tracking-wider">Interest Only - Debt Service & Cash Flow</h4>
            
            <div className="grid grid-cols-2 gap-6">
              {/* Monthly Column */}
              <div className="space-y-2">
                <p className="text-xs font-mono text-zinc-500 uppercase border-b border-zinc-800 pb-1">Monthly</p>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Rent Income</span>
                  <span className="font-mono text-green-400">${interestOnly.monthly_rent.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Interest Only Payment</span>
                  <span className="font-mono text-red-400">-${ioMonthlyInterest.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Taxes</span>
                  <span className="font-mono text-red-400">-${ioMonthlyTaxes.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Insurance</span>
                  <span className="font-mono text-red-400">-${ioMonthlyInsurance.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm pt-2 border-t border-zinc-800">
                  <span className="text-zinc-400">Total Debt Service</span>
                  <span className="font-mono font-bold">${ioTotalDebtService.toFixed(2)}</span>
                </div>
                <div className={`flex justify-between text-lg pt-2 ${ioNetCashFlow >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  <span className="font-mono uppercase">Net Cash Flow</span>
                  <span className="font-mono font-bold">${ioNetCashFlow.toFixed(2)}</span>
                </div>
              </div>

              {/* Annual Column */}
              <div className="space-y-2">
                <p className="text-xs font-mono text-zinc-500 uppercase border-b border-zinc-800 pb-1">Annualized</p>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Rent Income</span>
                  <span className="font-mono text-green-400">${(interestOnly.monthly_rent * 12).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Interest Only Payment</span>
                  <span className="font-mono text-red-400">-${(ioMonthlyInterest * 12).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Taxes</span>
                  <span className="font-mono text-red-400">-${interestOnly.annual_taxes.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Insurance</span>
                  <span className="font-mono text-red-400">-${interestOnly.annual_insurance.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm pt-2 border-t border-zinc-800">
                  <span className="text-zinc-400">Total Debt Service</span>
                  <span className="font-mono font-bold">${ioAnnualDebtService.toFixed(2)}</span>
                </div>
                <div className={`flex justify-between text-lg pt-2 ${ioAnnualNetCashFlow >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  <span className="font-mono uppercase">Net Cash Flow</span>
                  <span className="font-mono font-bold">${ioAnnualNetCashFlow.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Interest Only Schedule */}
          <div className="bg-zinc-900 border border-zinc-800 p-4">
            <h4 className="text-xs font-mono uppercase text-zinc-400 tracking-wider mb-3">
              Interest Only Schedule ({interestOnly.term_years} Year Term)
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm font-mono">
                <thead>
                  <tr className="text-xs text-zinc-500 uppercase border-b border-zinc-800">
                    <th className="text-left py-2">Year</th>
                    <th className="text-right py-2">Principal</th>
                    <th className="text-right py-2">Interest</th>
                    <th className="text-right py-2">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {ioSchedule.map(row => (
                    <tr key={row.year} className="border-b border-zinc-800/50">
                      <td className="py-2">{row.year}</td>
                      <td className="text-right text-zinc-500">$0</td>
                      <td className="text-right text-red-400">${row.interest.toFixed(0)}</td>
                      <td className="text-right">${interestOnly.loan_amount.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-zinc-500 mt-3">
              * Interest only loans require full principal repayment at maturity (balloon payment of ${interestOnly.loan_amount.toLocaleString()})
            </p>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
